<template>
    <!-- Modal Overlay -->
    <div
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]"
        v-if="showEditProgramModal"
        aria-labelledby="edit-program-modal"
        role="dialog"
        aria-modal="true"
        @click.self="closeModal()"
    >
        <!-- Modal Container -->
        <div
            class="flex w-full max-w-[600px] mx-4 p-6 flex-col items-start rounded-lg bg-white dark:bg-slate-800 shadow-lg shadow-black/10 dark:shadow-slate-900/50"
            @click.stop
        >
            <!-- Header -->
            <div class="flex items-center justify-between self-stretch pb-8">
                <!-- Title -->
                <h2 class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7">
                    Update points balance
                </h2>
                <!-- Close Button -->
                <button
                    class="flex p-2 items-center justify-center rounded border-0 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    @click="closeModal()"
                    type="button"
                    aria-label="Close modal"
                >
                    <Icon
                        icon="close"
                        :width="20"
                        :height="20"
                        class="text-slate-500 dark:text-slate-400"
                    />
                </button>
            </div>

            <!-- Content -->
            <div class="flex flex-col items-start self-stretch">
                <!-- Program Section -->
                <div
                    class="flex flex-col justify-center items-start self-stretch pb-6"
                >
                    <!-- Program Label -->
                    <div class="flex pb-2 items-start gap-2.5 self-stretch">
                        <span
                            class="flex-1 text-slate-600 dark:text-slate-400 text-sm font-medium leading-5"
                        >
                            Program
                        </span>
                    </div>
                    <!-- Program Info -->
                    <div class="flex items-center gap-3 self-stretch">
                        <!-- Program Image -->
                        <div
                            class="flex w-28 h-12 p-1.5 px-3.5 justify-center items-center rounded-lg border border-slate-200 dark:border-slate-700"
                        >
                            <img
                                v-if="reward.image || reward.program_image"
                                :src="
                                    getProgramImageUrl(
                                        reward.image || reward.program_image
                                    )
                                "
                                :alt="reward.name"
                                class="max-w-full max-h-full object-contain"
                            />
                        </div>
                        <!-- Program Name -->
                        <span
                            class="flex-1 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                        >
                            {{ reward.name }}
                        </span>
                    </div>
                </div>

                <!-- Input Fields -->
                <div
                    v-if="
                        (reward.connected &&
                            reward.program_user?.rewards_source === 'manual') ||
                        !reward.connected
                    "
                    class="flex items-start gap-4 self-stretch"
                >
                    <!-- Points Number Input -->
                    <UiInput
                        id="points-balance"
                        label="Points balance"
                        placeholder="Enter points"
                        v-model:inputValue="pointsValue"
                        input-format="thousands"
                        :options="{ wrapperClass: 'flex-1' }"
                    />

                    <!-- Date Input -->
                    <div class="flex flex-col items-start flex-1">
                        <!-- Label -->
                        <div class="flex pb-2 items-start gap-2.5 self-stretch">
                            <span
                                class="text-slate-600 dark:text-slate-400 text-sm font-medium leading-5"
                            >
                                Date
                            </span>
                        </div>
                        <!-- Input -->
                        <div
                            class="flex items-center gap-2 self-stretch rounded border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 relative"
                        >
                            <Icon
                                icon="calendar"
                                :width="16"
                                :height="16"
                                class="text-gray-400 absolute left-4 pointer-events-none z-10"
                            />
                            <flat-pickr
                                :config="dateConfig"
                                class="flex-1 text-slate-800 dark:text-slate-100 text-sm font-normal border-0 outline-0 bg-transparent py-1.5 pl-10 pr-4 w-full cursor-pointer placeholder:text-gray-400 dark:placeholder:text-slate-500"
                                v-model="balanceDate"
                                placeholder="Select date"
                            />
                        </div>
                    </div>
                </div>

                <!-- Error Messages -->
                <div>
                    <Transition name="fade">
                        <Errors :errors="errors" />
                    </Transition>
                </div>
            </div>

            <!-- Footer Buttons -->
            <div class="flex justify-end items-start gap-2 self-stretch pt-8">
                <!-- Cancel Button -->
                <button
                    type="button"
                    class="btn-base btn-white"
                    @click="closeModal()"
                >
                    Cancel
                </button>
                <!-- Update Button -->
                <button
                    type="button"
                    class="btn-base btn-dark"
                    @click="editReward"
                    :disabled="saveProgramBtnAction"
                >
                    <span v-if="saveProgramBtnAction" class="animate-spin">
                        ⟳
                    </span>
                    <span v-else>Update</span>
                </button>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { usePointsStore } from '@/store/points';
import { notify } from 'notiwind';
import dayjs from 'dayjs';

import ActionButton from '@/components/action-button.vue';
import Errors from '@/components/errors.vue';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import Icon from '@/components/ui/icon/icon.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import { handleError } from '@/utils/error-handler';

export default {
    components: {
        ActionButton,
        Errors,
        FlatPickr,
        Icon,
        UiInput,
    },
    data: () => ({
        errors: [],
        saveProgramBtnAction: false,
        balanceDate: dayjs().format('MM/DD/YYYY'),
        dateConfig: {
            dateFormat: 'm/d/Y',
            maxDate: 'today',
        },
    }),
    watch: {
        showEditProgramModal(newVal) {
            if (newVal) {
                this.balanceDate = dayjs().format('MM/DD/YYYY');
            }
        },
    },
    computed: {
        ...mapState(usePointsStore, [
            'programs',
            'reward',
            'user',
            'addRewardUser',
            'showProgramModal',
            'showEditProgramModal',
            'showPointsHistorySidebar',
        ]),
        cdnUrl() {
            return window.cdnUrl;
        },
        pointsValue: {
            get() {
                const p = this.reward.points;
                if (p === null || p === undefined || p === 0 || p === '0' || p === '') {
                    return '';
                }
                return p;
            },
            set(value) {
                const digits = String(value ?? '')
                    .replace(/\D/g, '')
                    .replace(/^0+(?=\d)/, '');
                this.reward.points = digits === '' ? '' : digits;
            },
        },
        actionBtnLabel() {
            return this.reward.connected &&
                this.reward.program_user?.rewards_source !== 'manual'
                ? 'Update'
                : 'Update Balance';
        },
        formattedPoints() {
            if (!this.reward.points) return '';
            return this.reward.points
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        hasEliteStatus: {
            get() {
                return this.reward.program_user
                    ? this.reward.program_user.has_elite_status
                    : null;
            },
            set(value) {
                if (this.reward.program_user === null) {
                    this.reward.program_user = {
                        has_elite_status: null,
                        elite_status: null,
                        id: null,
                    };
                }
                this.reward.program_user.has_elite_status = value;
            },
        },
        eliteStatus: {
            get() {
                return this.reward.program_user
                    ? this.reward.program_user.elite_status
                    : null;
            },
            set(value) {
                if (this.reward.program_user === null) {
                    this.reward.program_user = {
                        has_elite_status: null,
                        elite_status: null,
                        id: null,
                    };
                }
                this.reward.program_user.elite_status = value;
            },
        },
    },
    methods: {
        ...mapActions(usePointsStore, [
            'getRewards',
            'deleteProgram',
            'editProgram',
            'setProgramModal',
            'setEditProgramModal',
            'showProgramModalAction',
            'fetchPointsHistory',
        ]),
        getProgramImageUrl(image) {
            if (!image) return '';
            // Handle object format (program_image relation)
            if (typeof image === 'object' && image.path && image.file_name) {
                return `${this.cdnUrl}/${image.path}/_small/${image.file_name}`;
            }
            // Handle both formats: program_images/ and programs_images/
            const cleanImage = image.replace(/^programs?_images\//, '');
            return `https://my10x.nyc3.cdn.digitaloceanspaces.com/uploads/program_images/_small/${cleanImage}`;
        },
        editReward() {
            this.validateStep();
            if (this.errors.length === 0) {
                this.saveProgramBtnAction = true;
                if (!this.reward.connected) {
                    this.reward.balance_date = this.balanceDate;
                }

                if (
                    this.reward.connected &&
                    this.reward.program_user?.rewards_source !== 'manual'
                ) {
                    this.postProgram();
                } else {
                    this.putRewards();
                }
            }
        },
        putRewards() {
            // Priority: explicitly passed user > program_user > reward.user_id
            // this.user is the wallet user whose points are being edited (important for shared wallets)
            const userId =
                this.user?.id ||
                this.reward.program_user?.user_id ||
                this.reward.user_id;

            if (!userId) {
                this.errors.push({
                    message:
                        'Unable to identify user. Please refresh the page and try again.',
                });
                this.saveProgramBtnAction = false;
                return;
            }

            this.reward.has_elite_status = this.hasEliteStatus;
            this.reward.elite_status = this.eliteStatus;
            this.reward.user_id = userId;
            this.reward.user = { id: userId };
            // A program the member has no program_user row for yet carries
            // only its own id (MY10X-1F1).
            this.reward.program_id = this.reward.manual_reward_id
                ? this.reward.program_id
                : (this.reward.program_user?.program_id ?? this.reward.id);
            const method = this.reward.manual_reward_id ? 'put' : 'post';
            const id = this.reward.manual_reward_id
                ? this.reward.manual_reward_id
                : null;
            const path = this.reward.manual_reward_id
                ? '/rewards/' + id
                : '/rewards';
            ApiService.apiCall(path, method, this.reward)
                .then(() => {
                    if (import.meta.env.VITE_APP_ENV === 'production') {
                        woopra.track('program-updated-my10x', {
                            program: this.reward.name,
                            balance: this.reward.points,
                            uuid: localStorage.getItem('uuid'),
                        });
                    }

                    notify({
                        group: 'success',
                        title: 'Points updated',
                        text: `${this.reward.name} points were successfully updated.`,
                    });

                    setTimeout(() => {
                        this.saveProgramBtnAction = false;
                        this.closeModal();
                        this.balanceDate = dayjs().format('MM/DD/YYYY');
                    }, 500);
                })
                .catch((error) => {
                    handleError(error);
                    this.saveProgramBtnAction = false;
                });
        },
        postProgram() {
            // Priority: explicitly passed user > program_user > reward.user_id
            // this.user is the wallet user whose points are being edited (important for shared wallets)
            const userId =
                this.user?.id ||
                this.reward.program_user?.user_id ||
                this.reward.user_id;

            if (!userId) {
                this.errors.push({
                    message:
                        'Unable to identify user. Please refresh the page and try again.',
                });
                this.saveProgramBtnAction = false;
                return;
            }

            let method = this.reward.program_user?.id ? 'put' : 'post';
            let id = this.reward.program_user?.id
                ? this.reward.program_user.id
                : this.reward.id;
            this.reward.has_elite_status = this.hasEliteStatus;
            this.reward.elite_status = this.eliteStatus;
            this.reward.user_id = userId;
            ApiService.apiCall('/program-user/' + id, method, this.reward)
                .then(() => {
                    setTimeout(() => {
                        this.saveProgramBtnAction = false;
                        this.$emit('close');
                    }, 500);
                })
                .catch((error) => {
                    handleError(error);
                    this.saveProgramBtnAction = false;
                });
        },
        validateStep() {
            this.errors = [];
            if (this.reward.points === '' && !this.reward.connected) {
                this.errors.push({
                    message: 'Please enter your reward points.',
                });
            }
            if (
                (this.balanceDate === '' || !this.balanceDate) &&
                !this.reward.connected
            ) {
                this.errors.push({ message: 'Please enter your points date.' });
            }
            if (
                this.hasEliteStatus &&
                this.hasEliteStatus === 'Y' &&
                !this.eliteDtatus
            ) {
                //this.errors.push({ message: 'Please enter your elite status.' });
            }
        },
        closeModal() {
            this.errors = [];
            this.getRewards();
            // Refresh sidebar history if it's open
            if (this.showPointsHistorySidebar) {
                this.fetchPointsHistory();
            }
            this.setEditProgramModal(false);
        },
    },
    created() {},
    mounted() {},
};
</script>
<style lang="scss" scoped>
.steps {
    position: relative;
    height: 100%;
}
.step {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
}
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
